import { useState } from "react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { inputCls } from "@/components/forms";

type Application = {
  id: string;
  full_name: string;
  email: string;
  city: string;
  referrer?: string | null;
  heard_about: string;
  phone?: string | null;
  status?: string;
  created_at: string;
};

export default function Steward() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem("steward_token") || "",
  );
  const [authed, setAuthed] = useState(false);
  const [apps, setApps] = useState<Application[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string>("");

  const load = async (t: string) => {
    setError(null);
    try {
      const res = await api.get("/export/applications", {
        headers: { "X-Admin-Token": t },
      });
      setApps(res.data.data);
      setAuthed(true);
      sessionStorage.setItem("steward_token", t);
    } catch {
      setError("That token was not recognized.");
      sessionStorage.removeItem("steward_token");
    }
  };

  useState(() => {
    if (token) load(token);
  });

  const accept = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await api.post(
        `/applications/${id}/accept`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      setApps((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: "accepted" } : a)),
      );
    } catch {
      setError("The acceptance could not be completed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (!authed) {
    return (
      <div data-testid="steward-gate" className="flex min-h-screen items-center justify-center bg-paper px-6">
        <Reveal className="w-full max-w-sm text-center">
          <p className="font-oswald text-[11px] uppercase tracking-[0.4em] text-seaglass">
            Private
          </p>
          <h1 className="mt-4 font-garamond text-4xl text-ink">Steward</h1>
          <p className="mt-4 font-garamond text-base text-softblack/80">
            This page is for Syndeō only. Enter the admin token to continue.
          </p>
          <form
            className="mt-8 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              load(token);
            }}
          >
            <input
              type="password"
              data-testid="steward-token"
              placeholder="Admin token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className={inputCls}
            />
            {error && (
              <p data-testid="steward-error" className="font-garamond text-sm text-walnut">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="steward-enter"
              className="w-full border border-ink px-8 py-3 font-oswald text-xs uppercase tracking-[0.35em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Enter
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  return (
    <div data-testid="steward-page" className="min-h-screen bg-paper px-6 py-16">
      <div className="mx-auto max-w-3xl">
        <p className="font-oswald text-[11px] uppercase tracking-[0.4em] text-seaglass">
          Steward
        </p>
        <h1 className="mt-4 font-garamond text-4xl text-ink">
          Access Requests
        </h1>
        <p className="mt-3 font-garamond text-base text-softblack/80">
          {apps.length} on record. Accepting grants membership and sends the
          invitation email immediately.
        </p>

        {error && (
          <p data-testid="steward-list-error" className="mt-6 border border-walnut/40 bg-chalk px-4 py-3 font-garamond text-base text-walnut">
            {error}
          </p>
        )}

        <div className="mt-10 space-y-px border border-stone/60 bg-stone/50">
          {apps.map((a) => (
            <div
              key={a.id}
              data-testid={`steward-row-${a.id}`}
              className="flex flex-col gap-4 bg-paper p-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-garamond text-xl text-ink">
                  {a.full_name}
                  {a.status === "accepted" && (
                    <span className="ml-3 font-oswald text-[10px] uppercase tracking-[0.25em] text-seaglass">
                      Accepted
                    </span>
                  )}
                </p>
                <p className="mt-1 font-garamond text-base text-softblack/80">
                  {a.email} · {a.city}
                </p>
                <p className="mt-1 font-garamond text-sm italic text-softblack/60">
                  {a.heard_about}
                  {a.referrer ? ` · via ${a.referrer}` : ""} ·{" "}
                  {new Date(a.created_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
              {a.status !== "accepted" && (
                <button
                  type="button"
                  data-testid={`steward-accept-${a.id}`}
                  disabled={busy === a.id}
                  onClick={() => accept(a.id)}
                  className="shrink-0 border border-ink px-8 py-3 font-oswald text-xs uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-50"
                >
                  {busy === a.id ? "Accepting…" : "Accept"}
                </button>
              )}
            </div>
          ))}
          {apps.length === 0 && (
            <p className="bg-paper p-10 text-center font-garamond text-lg italic text-softblack/60">
              No requests yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
